import { useCallback, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/api';
import { queryKeys } from './queryKeys';

const fetchEventsPage = async (page, filters = {}) => {
  const params = new URLSearchParams({ page: String(page) });
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.showOnMainPage) params.set('showOnMainPage', 'true');
  const response = await api.get(`/events?${params.toString()}`);
  return response.data;
};

export const useEvents = (filters = {}) => {
  const [extraData, setExtraData] = useState([]);
  const [pageN, setPageN] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { data: queryData, isLoading: isInitialLoading } = useQuery({
    queryKey: [...queryKeys.events.page(1), filters],
    queryFn: () => fetchEventsPage(1, filters),
  });

  // When the initial page refetches after a mutation, reset extra pages.
  useEffect(() => {
    if (queryData?.data !== undefined) {
      setExtraData([]);
      setPageN(1);
      setHasMore(queryData?.hasMore ?? true);
    }
  }, [queryData?.data, queryData?.hasMore]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = pageN + 1;
    try {
      const response = await fetchEventsPage(nextPage, filters);
      const items = response?.data || [];
      setExtraData((prev) => [...prev, ...items]);
      setPageN(nextPage);
      setHasMore(response?.hasMore ?? items.length > 0);
    } catch (error) {
      console.error('Failed to fetch more events:', error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pageN, isLoadingMore, hasMore, filters]);

  const finalData = useMemo(() => [...(queryData?.data ?? []), ...extraData], [queryData?.data, extraData]);

  return {
    data: finalData,
    isLoading: isInitialLoading || isLoadingMore,
    hasMore,
    loadMore,
  };
};

export const useLandingPageEvents = () => {
  return useQuery({
    queryKey: queryKeys.events.landing(),
    queryFn: async () => {
      const response = await api.get(`/events/landing`);
      return response.data.data;
    },
  });
};
